import React, { useState, useEffect, useCallback } from 'react';
import { FaLayerGroup, FaCogs, FaInfoCircle, FaPlus, FaEdit, FaTrash, FaList } from 'react-icons/fa';
import { Alert, Dropdown } from 'react-bootstrap';
import CreateSelectionGroupModal from './components/CreateSelectionGroupModal/CreateSelectionGroupModal';
import CreateModifierModal from './components/CreateModifierModal/CreateModifierModal';
import ExistingGroupSelector from './components/ExistingGroupSelector/ExistingGroupSelector';
import ExistingModifierSelector from './components/ExistingModifierSelector/ExistingModifierSelector';
import { GetSelectionGroupsForItemById } from '../../../../../../../../../../../../crud/menu_api/selection_groups';
import { GetModifiersForItemById } from '../../../../../../../../../../../../crud/menu_api/selection_modifiers';
import './AdditionsInfoSection.scss';

// Types for the additions system
interface SelectionGroupAddition {
  selection_group_id?: number; // Present if existing group
  selection_group_name: string;
  selection_group_description?: string;
  is_new: boolean; // True if creating new group
  items_contained: SelectionItemAddition[];
  // Configuration for linking to current item
  min_variance: number;
  max_variance: number;
  min_selections: number;
  max_selections: number;
  is_price_modifier: boolean;
}

interface SelectionItemAddition {
  item_id: number;
  item_name: string;
  label: string;
  is_new: boolean; // Always false for existing items
  // Price modifiers for all channels
  price_modifier: number;
  delivery_price_modifier: number;
  online_price_modifier: number;
  desk_price_modifier: number;
  // Selection constraints
  min_selections: number;
  max_selections: number;
}

interface ModifierAddition {
  modifier_id?: number; // Present if existing modifier
  modifier_name: string;
  modifier_description?: string;
  is_new: boolean; // True if creating new modifier
  options_included: ModifierOptionAddition[];
  // Configuration for linking to current item
  is_optional: boolean;
  is_price_setter: boolean;
}

interface ModifierOptionAddition {
  option_id?: number; // Present if existing option
  option_name: string;
  option_description?: string;
  is_new: boolean; // True if creating new option
  price_modifier: number;
}

interface AdditionsPayload {
  selection_groups: SelectionGroupAddition[];
  modifiers: ModifierAddition[];
}

interface AdditionsInfoSectionProps {
  itemId?: number; // Present in update mode
  itemName?: string; // Current item name for display in modals
  additions?: AdditionsPayload; // Current additions state from parent
  onAdditionsChange?: (additions: AdditionsPayload) => void; // Callback to pass data to parent
}

const AdditionsInfoSection: React.FC<AdditionsInfoSectionProps> = ({ 
  itemId, 
  itemName,
  additions: propAdditions,
  onAdditionsChange 
}) => {
  // Initialize local state from props - this ensures state is restored when component remounts
  // after tab switching (conditional rendering causes unmount/remount)
  const [additions, setAdditions] = useState<AdditionsPayload>(() => {
    // Use propAdditions if available, otherwise use default empty state
    return propAdditions || {
      selection_groups: [],
      modifiers: []
    };
  });
  
  const [activeTab, setActiveTab] = useState<'groups' | 'modifiers'>('groups');
  const [loading, setLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  // Track if we've already loaded data from API for this item (update mode)
  const [apiDataLoaded, setApiDataLoaded] = useState(false);

  // Modal states
  const [showCreateSelectionGroupModal, setShowCreateSelectionGroupModal] = useState(false);
  const [showCreateModifierModal, setShowCreateModifierModal] = useState(false);
  const [showExistingGroupSelector, setShowExistingGroupSelector] = useState(false);
  const [showExistingModifierSelector, setShowExistingModifierSelector] = useState(false);
  
  // Edit states
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null);
  const [editingModifierIndex, setEditingModifierIndex] = useState<number | null>(null);

  // Sync local state with propAdditions when component mounts or propAdditions changes
  // This is crucial for tab switching - when the component remounts, it needs to restore
  // the state from the parent component's additions state
  useEffect(() => {
    if (propAdditions) {
      setAdditions(propAdditions);
      // Mark as initialized if propAdditions was provided (parent already has state)
      setInitialDataLoaded(true);
    }
  }, []); // Only run on mount to restore state from parent

  // Load existing data when in update mode (only if we don't already have data from props)
  const loadExistingItemData = useCallback(async () => {
    if (!itemId) return;
    
    // If parent already provided additions with data, don't reload from API
    if (propAdditions && (propAdditions.selection_groups.length > 0 || propAdditions.modifiers.length > 0)) {
      setInitialDataLoaded(true);
      setApiDataLoaded(true);
      return;
    }
    
    setLoading(true);
    try {
      // Load existing selection groups for this item
      const [groupsResponse, modifiersResponse] = await Promise.all([
        GetSelectionGroupsForItemById(itemId),
        GetModifiersForItemById(itemId)
      ]);

      const loadedAdditions = {
        selection_groups: groupsResponse.data || [],
        modifiers: modifiersResponse.data || []
      };

      setAdditions(loadedAdditions);
      setInitialDataLoaded(true);
      setApiDataLoaded(true);
      
      // Notify parent of the loaded data
      if (onAdditionsChange) {
        onAdditionsChange(loadedAdditions);
      }
    } catch (error) {
      console.error('Error loading existing item data:', error);
      setInitialDataLoaded(true); // Set to true even on error to prevent infinite retries
      setApiDataLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [itemId, onAdditionsChange, propAdditions]);

  // Reset state when itemId changes (new item opened in modal)
  useEffect(() => {
    // Reset loading flags to allow fresh data load
    setApiDataLoaded(false);
    setInitialDataLoaded(false);
  }, [itemId]);

  useEffect(() => {
    if (itemId && !apiDataLoaded) {
      // For update mode: load from API only if we haven't already
      loadExistingItemData();
    } else if (!itemId && !initialDataLoaded) {
      // For create mode, set initialDataLoaded to true immediately
      setInitialDataLoaded(true);
    }
  }, [itemId, apiDataLoaded, initialDataLoaded, loadExistingItemData]);

  // Helper function to convert SelectionGroupAddition to modal format
  const convertGroupToModalFormat = (group: SelectionGroupAddition, index: number) => {
    return {
      id: group.selection_group_id?.toString() || `temp_${index}`,
      name: group.selection_group_name,
      min_selections: group.min_selections,
      max_selections: group.max_selections,
      min_variance: group.min_variance,
      max_variance: group.max_variance,
      items: group.items_contained.map(item => ({
        item_id: item.item_id,
        item_name: item.item_name,
        price_modifier: item.price_modifier,
        delivery_price_modifier: item.delivery_price_modifier,
        online_price_modifier: item.online_price_modifier,
        desk_price_modifier: item.desk_price_modifier,
        min_selections: item.min_selections,
        max_selections: item.max_selections
      }))
    };
  };

  // Helper function to convert ModifierAddition to modal format
  const convertModifierToModalFormat = (modifier: ModifierAddition, index: number) => {
    return {
      id: modifier.modifier_id?.toString() || `temp_${index}`,
      name: modifier.modifier_name,
      price_setter: modifier.is_price_setter ? 'replace' as const : 'add' as const,
      mandatory: !modifier.is_optional,
      options: modifier.options_included.map(option => ({
        id: option.option_id?.toString() || `temp_option_${Date.now()}`,
        name: option.option_name,
        price_delivery: option.price_modifier,
        price_online: option.price_modifier,
        price_desk: option.price_modifier
      }))
    };
  };

  // Update parent when additions change
  useEffect(() => {
    if (onAdditionsChange && initialDataLoaded) {
      onAdditionsChange(additions);
    }
  }, [additions, onAdditionsChange, initialDataLoaded]);

  const generateModifierMessage = (modifier: ModifierAddition, itemName: string) => {
    const isOptional = modifier.is_optional;
    const isReplacePrice = modifier.is_price_setter;
    
    if (isOptional) {
      if (isReplacePrice) {
        return `El cliente podrá elegir una opción de manera opcional. El precio de la opción reemplaza completamente el precio base del producto:`;
      } else {
        return `El cliente podrá elegir una opción de manera opcional. El precio de la opción se suma al precio base del producto:`;
      }
    } else {
      if (isReplacePrice) {
        return `El cliente debe seleccionar una opción antes de continuar. El precio de la opción reemplaza completamente el precio base del producto:`;
      } else {
        return `El cliente debe seleccionar una opción antes de continuar. El precio de la opción se suma al precio base del producto:`;
      }
    }
  };

  const generateGroupMessage = (group: SelectionGroupAddition, itemName: string) => {
    const { min_selections, max_selections, min_variance, max_variance } = group;
    const baseItem = itemName || 'este producto';
    
    // Case 1: Optional single selection (0-1 selections, 0-1 variance)
    if (min_selections === 0 && max_selections === 1 && min_variance === 0 && max_variance === 1) {
      return `El cliente podrá elegir uno de los siguientes productos de manera opcional:`;
    }
    
    // Case 2: Required single selection (1-1 selections, 0-1 variance)
    if (min_selections === 1 && max_selections === 1 && min_variance === 0 && max_variance === 1) {
      return `El cliente deberá elegir uno de los siguientes productos:`;
    }
    
    // Case 3: Multiple required selections (same min/max)
    if (min_selections === max_selections && min_selections > 1) {
      if (min_variance === max_variance) {
        return `El cliente deberá elegir exactamente ${min_selections} productos (${min_variance === 1 ? 'del mismo tipo' : `máximo ${min_variance} de cada tipo`}) de la siguiente lista:`;
      } else {
        return `El cliente deberá elegir exactamente ${min_selections} productos, eligiendo entre ${min_variance} y ${max_variance} de cada tipo, de la siguiente lista:`;
      }
    }
    
    // Case 4: Range of selections
    if (min_selections !== max_selections) {
      if (min_selections === 0) {
        return `El cliente podrá elegir hasta ${max_selections} productos opcionalmente, con un máximo de ${max_variance} de cada tipo, de la siguiente lista:`;
      } else {
        return `El cliente deberá elegir entre ${min_selections} y ${max_selections} productos, eligiendo entre ${min_variance} y ${max_variance} de cada tipo, de la siguiente lista:`;
      }
    }
    
    // Fallback to original message for complex cases
    return `Cuando tu cliente compre un ${baseItem}, podrá elegir entre ${min_selections} y ${max_selections} productos, eligiendo al menos ${min_variance} y a lo más ${max_variance} de la siguiente lista:`;
  };

  const renderEducationalCards = () => (
    <div className="additions-education-cards">
      <Alert variant="info" className="educational-alert">
        <div className="education-content">
          <FaInfoCircle className="education-icon" />
          <div className="education-text">
            <h5>¿Cuándo usar cada tipo de personalización?</h5>
            <p className="education-subtitle">Elige el tipo de personalización que mejor se adapte a tu producto:</p>
          </div>
        </div>
      </Alert>
      
      <div className="addition-type-cards">
        <div 
          className={`addition-type-card ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          {activeTab === 'groups' && (
            <div className="status-badge">Configurando</div>
          )}
          <div className="card-icon">
            <FaLayerGroup />
          </div>
          <div className="card-content">
            <h6>Combos y Extras</h6>
            <p>Para productos físicos con costo e inventario</p>
            <div className="examples">
              <strong>Ejemplos:</strong> Queso extra, Papas fritas, Bebidas
            </div>
            {additions.selection_groups.length > 0 && (
              <div className="card-count">
                {additions.selection_groups.length} grupo{additions.selection_groups.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        <div 
          className={`addition-type-card ${activeTab === 'modifiers' ? 'active' : ''}`}
          onClick={() => setActiveTab('modifiers')}
        >
          {activeTab === 'modifiers' && (
            <div className="status-badge">Configurando</div>
          )}
          <div className="card-icon">
            <FaCogs />
          </div>
          <div className="card-content">
            <h6>Modificadores</h6>
            <p>Para características intangibles sin inventario</p>
            <div className="examples">
              <strong>Ejemplos:</strong> Tamaños, Temperatura, Nivel de cocción
            </div>
            {additions.modifiers.length > 0 && (
              <div className="card-count">
                {additions.modifiers.length} modificador{additions.modifiers.length !== 1 ? 'es' : ''}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );


  const renderSelectionGroups = () => (
    <div className="additions-section">
      <div className="section-header">
        <h4>
          <FaLayerGroup />
          Configuración de Combos y Extras
        </h4>
        <Dropdown className="add-dropdown">
          <Dropdown.Toggle variant="outline-primary">
            <FaPlus className="me-2" />
            Agregar Grupo
          </Dropdown.Toggle>
          
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => {
              setEditingGroupIndex(null);
              setShowCreateSelectionGroupModal(true);
            }}>
              <FaPlus className="me-2" />
              Crear Nuevo Grupo
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setShowExistingGroupSelector(true)}>
              <FaList className="me-2" />
              Seleccionar Existente
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {additions.selection_groups.length === 0 ? (
        <div className="empty-state-selection">
          <FaLayerGroup className="empty-icon" />
          <h5>No hay grupos de selección configurados</h5>
          <p>Agrega grupos para que los clientes puedan personalizar este ítem con productos adicionales</p>
        </div>
      ) : (
        <div className="additions-list">
          {additions.selection_groups.map((group, index) => (
            <div key={index} className="addition-card group-card">
              <div className="card-header">
                <div className="card-title">
                  <span className="group-name">{group.selection_group_name}</span>
                  {group.is_new && <span className="new-badge">Nuevo</span>}
                </div>
                <div className="card-actions">
                  <button 
                    type="button"
                    className="edit-btn" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditSelectionGroup(index);
                    }}
                  >
                    <FaEdit />
                  </button>
                  <button 
                    type="button"
                    className="delete-btn" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveSelectionGroup(index);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              {group.selection_group_description && (
                <p className="group-description">{group.selection_group_description}</p>
              )}
              <div className="group-summary">
                <p className="summary-text">
                  {generateGroupMessage(group, itemName || 'este producto')}
                </p>
                <div className="items-badges">
                  {group.items_contained.map((item, itemIndex) => (
                    <span key={itemIndex} className="item-badge">
                      {item.item_name}
                      {item.price_modifier !== 0 && (
                        <span className="price-modifier">
                          {item.price_modifier > 0 ? '+' : ''}${item.price_modifier}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderModifiers = () => (
    <div className="additions-section">
      <div className="section-header">
        <h4>
          <FaCogs />
          Configuración de Modificadores
        </h4>
        <Dropdown className="add-dropdown">
          <Dropdown.Toggle variant="outline-primary">
            <FaPlus className="me-2" />
            Agregar Modificador
          </Dropdown.Toggle>
          
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => {
              setEditingModifierIndex(null);
              setShowCreateModifierModal(true);
            }}>
              <FaPlus className="me-2" />
              Crear Nuevo Modificador
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setShowExistingModifierSelector(true)}>
              <FaList className="me-2" />
              Seleccionar Existente
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {additions.modifiers.length === 0 ? (
        <div className="empty-state">
          <FaCogs className="empty-icon" />
          <h5>No hay modificadores configurados</h5>
          <p>Agrega modificadores para que los clientes puedan personalizar las características de este ítem</p>
        </div>
      ) : (
        <div className="additions-list">
          {additions.modifiers.map((modifier, index) => (
            <div key={index} className="addition-card modifier-card">
              <div className="card-header">
                <div className="card-title">
                  <span className="modifier-name">{modifier.modifier_name}</span>
                  {modifier.is_new && <span className="new-badge">Nuevo</span>}
                </div>
                <div className="card-actions">
                  <button 
                    type="button"
                    className="edit-btn" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditModifier(index);
                    }}
                  >
                    <FaEdit />
                  </button>
                  <button 
                    type="button"
                    className="delete-btn" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveModifier(index);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              {modifier.modifier_description && (
                <p className="modifier-description">{modifier.modifier_description}</p>
              )}
              <div className="modifier-summary">
                <p className="summary-text">
                  {generateModifierMessage(modifier, itemName || 'este producto')}
                </p>
                <div className="options-badges">
                  {modifier.options_included.map((option, optionIndex) => (
                    <span key={optionIndex} className="option-badge">
                      {option.option_name}
                      {option.price_modifier !== 0 && (
                        <span className="price-modifier">
                          {option.price_modifier > 0 ? '+' : ''}${option.price_modifier}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Handler functions
  const handleEditSelectionGroup = (index: number) => {
    setEditingGroupIndex(index);
    setShowCreateSelectionGroupModal(true);
  };

  const handleRemoveSelectionGroup = (index: number) => {
    const updatedAdditions = {
      ...additions,
      selection_groups: additions.selection_groups.filter((_, i) => i !== index)
    };
    setAdditions(updatedAdditions);
  };

  const handleEditModifier = (index: number) => {
    setEditingModifierIndex(index);
    setShowCreateModifierModal(true);
  };

  const handleRemoveModifier = (index: number) => {
    const updatedAdditions = {
      ...additions,
      modifiers: additions.modifiers.filter((_, i) => i !== index)
    };
    setAdditions(updatedAdditions);
  };

  // Modal handlers
  const handleSelectionGroupCreated = (newGroup: any) => {
    // Convert the modal data to our internal format
    const selectionGroupAddition: SelectionGroupAddition = {
      selection_group_id: newGroup.id.startsWith('temp_') ? undefined : parseInt(newGroup.id),
      selection_group_name: newGroup.name,
      is_new: true,
      items_contained: (newGroup.items || []).map((item: any) => ({
        item_id: item.item_id || 0,
        item_name: item.item_name,
        label: item.item_name,
        is_new: item.item_id === 0,
        price_modifier: item.price_modifier || 0,
        delivery_price_modifier: item.delivery_price_modifier || 0,
        online_price_modifier: item.online_price_modifier || 0,
        desk_price_modifier: item.desk_price_modifier || 0,
        min_selections: item.min_selections || 0,
        max_selections: item.max_selections || 1
      })),
      min_variance: newGroup.min_variance,
      max_variance: newGroup.max_variance,
      min_selections: newGroup.min_selections,
      max_selections: newGroup.max_selections,
      is_price_modifier: true
    };

    let updatedGroups;
    if (editingGroupIndex !== null) {
      // Update existing group
      updatedGroups = [...additions.selection_groups];
      updatedGroups[editingGroupIndex] = selectionGroupAddition;
      setEditingGroupIndex(null);
    } else {
      // Add new group
      updatedGroups = [...additions.selection_groups, selectionGroupAddition];
    }

    const updatedAdditions = {
      ...additions,
      selection_groups: updatedGroups
    };
    setAdditions(updatedAdditions);
  };

  const handleModifierCreated = (newModifier: any) => {
    // Convert the modal data to our internal format
    const modifierAddition: ModifierAddition = {
      modifier_id: newModifier.id.startsWith('temp_') ? undefined : parseInt(newModifier.id),
      modifier_name: newModifier.name,
      is_new: true,
      options_included: newModifier.options.map((option: any) => ({
        option_id: option.id.startsWith('temp_') ? undefined : parseInt(option.id),
        option_name: option.name,
        is_new: true,
        price_modifier: option.price_desk
      })),
      is_optional: !newModifier.mandatory,
      is_price_setter: newModifier.price_setter === 'replace'
    };

    let updatedModifiers;
    if (editingModifierIndex !== null) {
      // Update existing modifier
      updatedModifiers = [...additions.modifiers];
      updatedModifiers[editingModifierIndex] = modifierAddition;
      setEditingModifierIndex(null);
    } else {
      // Add new modifier
      updatedModifiers = [...additions.modifiers, modifierAddition];
    }

    const updatedAdditions = {
      ...additions,
      modifiers: updatedModifiers
    };
    setAdditions(updatedAdditions);
  };

  // Handler for selecting existing groups
  const handleExistingGroupSelected = (group: any) => {
    const updatedAdditions = {
      ...additions,
      selection_groups: [...additions.selection_groups, group]
    };
    setAdditions(updatedAdditions);
  };

  // Handler for selecting existing modifiers
  const handleExistingModifierSelected = (modifier: any) => {
    const updatedAdditions = {
      ...additions,
      modifiers: [...additions.modifiers, modifier]
    };
    setAdditions(updatedAdditions);
  };

  if (loading) {
    return (
      <div className="additions-loading">
        <div className="loading-spinner"></div>
        <p>Cargando opciones de personalización...</p>
      </div>
    );
  }

  return (
    <div className="additions-info-section">
      {renderEducationalCards()}
      
      <div className="additions-content">
        {activeTab === 'groups' && renderSelectionGroups()}
        {activeTab === 'modifiers' && renderModifiers()}
      </div>

      {/* Modals */}
      <CreateSelectionGroupModal
        show={showCreateSelectionGroupModal}
        onHide={() => {
          setShowCreateSelectionGroupModal(false);
          setEditingGroupIndex(null);
        }}
        onSave={handleSelectionGroupCreated}
        currentItemName={itemName || 'Nuevo Item'}
        editMode={editingGroupIndex !== null}
        editData={editingGroupIndex !== null && editingGroupIndex < additions.selection_groups.length 
          ? convertGroupToModalFormat(additions.selection_groups[editingGroupIndex], editingGroupIndex)
          : undefined
        }
      />
      
      <CreateModifierModal
        show={showCreateModifierModal}
        onHide={() => {
          setShowCreateModifierModal(false);
          setEditingModifierIndex(null);
        }}
        onSave={handleModifierCreated}
        currentItemName={itemName || 'Nuevo Item'}
        editMode={editingModifierIndex !== null}
        editData={editingModifierIndex !== null && editingModifierIndex < additions.modifiers.length 
          ? convertModifierToModalFormat(additions.modifiers[editingModifierIndex], editingModifierIndex)
          : undefined
        }
      />

      <ExistingGroupSelector
        show={showExistingGroupSelector}
        onHide={() => setShowExistingGroupSelector(false)}
        onGroupSelected={handleExistingGroupSelected}
        currentGroups={additions.selection_groups}
        itemName={itemName}
      />

      <ExistingModifierSelector
        show={showExistingModifierSelector}
        onHide={() => setShowExistingModifierSelector(false)}
        onModifierSelected={handleExistingModifierSelected}
        currentModifiers={additions.modifiers}
        itemName={itemName}
      />
    </div>
  );
};

export default AdditionsInfoSection;